import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getVehicles, deleteVehicle } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Truck, 
  Loader2, 
  MapPin, 
  Package,
  Settings,
  Trash2,
  Edit,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const FleetPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const data = await getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteVehicle(id);
      toast.success('Véhicule supprimé');
      fetchVehicles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getVehicleTypeLabel = (type) => {
    const labels = {
      truck: 'Camion',
      van: 'Fourgon',
      refrigerated: 'Frigorifique',
      flatbed: 'Plateau',
      tanker: 'Citerne'
    };
    return labels[type] || type;
  };

  const getVehicleIcon = (type) => {
    return Truck;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="fleet-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              Ma Flotte
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez vos véhicules et leurs disponibilités
            </p>
          </div>
          <Link to="/fleet/new">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un véhicule
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="stat-value text-xl">{vehicles.length}</div>
                  <div className="stat-label">Véhicules</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="stat-value text-xl">
                    {vehicles.filter(v => v.is_available).length}
                  </div>
                  <div className="stat-label">Disponibles</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vehicles List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((vehicle) => {
              const VehicleIcon = getVehicleIcon(vehicle.vehicle_type);
              return (
                <Card key={vehicle.id} className="hover:shadow-lg transition-shadow" data-testid={`vehicle-card-${vehicle.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                          <VehicleIcon className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{vehicle.name}</h3>
                          <p className="text-sm text-slate-500">{vehicle.license_plate}</p>
                        </div>
                      </div>
                      <Badge className={vehicle.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                        {vehicle.is_available ? 'Disponible' : 'Indisponible'}
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Type</span>
                        <span className="font-medium text-slate-900">{getVehicleTypeLabel(vehicle.vehicle_type)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Capacité</span>
                        <span className="font-['JetBrains_Mono'] text-slate-900">{vehicle.capacity_kg} kg / {vehicle.capacity_m3} m³</span>
                      </div>
                      {vehicle.certifications?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {vehicle.certifications.map((cert, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-4 border-t">
                      <Link to={`/fleet/${vehicle.id}/edit`} className="flex-1">
                        <Button variant="outline" className="w-full" size="sm">
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. Le véhicule sera définitivement supprimé.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(vehicle.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Truck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun véhicule</h3>
                <p className="text-slate-500 mb-4">
                  Ajoutez vos véhicules pour commencer à recevoir des opportunités
                </p>
                <Link to="/fleet/new">
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un véhicule
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FleetPage;
